import { Radio, Play } from "lucide-react";
import { toast } from "react-toastify";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useMatchesAdmin } from "@/hooks/api/useMatches";
import { useUpdateMatchStatus } from "@/hooks/api/useMatches";

export default function NoLiveMatches() {
  const { data, isLoading } = useMatchesAdmin({ status: "SCHEDULED", limit: 5, sortBy: "kickoff" });
  const { mutate: updateStatus, isPending } = useUpdateMatchStatus();

  const upcoming = data?.matches ?? [];

  const handleStartMatch = (match: { id: number; homeNation: { name: string }; awayNation: { name: string } }) => {
    if (!confirm(`Start match: ${match.homeNation.name} vs ${match.awayNation.name}?\n\nThis will set it to LIVE and start the timer.`)) return;
    updateStatus(
      { id: match.id, status: "LIVE" },
      {
        onSuccess: () => toast.success(`🟢 Match started — head to the live scorer!`),
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onError: (e: any) => toast.error(e.response?.data?.message || "Failed to start match"),
      }
    );
  };

  return (
    <div className="space-y-6">
      {/* Empty state header */}
      <div className="flex flex-col items-center justify-center h-40 bg-white rounded-xl border-2 border-dashed border-gray-200">
        <Radio className="h-10 w-10 text-gray-300 mb-3" />
        <h3 className="text-lg font-semibold text-gray-500">No Live Matches</h3>
        <p className="text-sm text-gray-400 mt-1">There are no matches currently in progress.</p>
      </div>

      {/* Upcoming matches */}
      {!isLoading && upcoming.length > 0 && (
        <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
          <div className="px-4 py-3 border-b bg-gray-50">
            <h4 className="text-sm font-semibold text-gray-700">Upcoming Scheduled Matches</h4>
            <p className="text-xs text-gray-400 mt-0.5">Click "Start" to go live</p>
          </div>
          <div className="divide-y">
            {upcoming.map((match) => {
              const kickoff = new Date(match.kickoffTime);
              const isNearKickoff = kickoff.getTime() - Date.now() < 30 * 60 * 1000; // within 30min
              return (
                <div key={match.id} className="flex items-center gap-4 px-4 py-3">
                  {/* Teams */}
                  <div className="flex items-center gap-2 flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      {match.homeNation.flagUrl && (
                        <img src={match.homeNation.flagUrl} className="w-6 h-4 object-cover rounded" alt="" />
                      )}
                      <span className="font-medium text-sm text-gray-900 truncate">{match.homeNation.name}</span>
                    </div>
                    <span className="text-gray-400 text-xs font-bold shrink-0">vs</span>
                    <div className="flex items-center gap-1.5">
                      {match.awayNation.flagUrl && (
                        <img src={match.awayNation.flagUrl} className="w-6 h-4 object-cover rounded" alt="" />
                      )}
                      <span className="font-medium text-sm text-gray-900 truncate">{match.awayNation.name}</span>
                    </div>
                  </div>

                  {/* Stage */}
                  {match.stage && (
                    <span className="text-xs text-gray-400 hidden sm:block shrink-0">{match.stage}</span>
                  )}

                  {/* Kickoff time */}
                  <div className="text-right shrink-0">
                    <div className={`text-xs font-medium ${isNearKickoff ? "text-orange-600" : "text-gray-500"}`}>
                      {kickoff.toLocaleDateString("en-US", { month: "short", day: "numeric" })}
                    </div>
                    <div className={`text-xs ${isNearKickoff ? "text-orange-500 font-bold" : "text-gray-400"}`}>
                      {kickoff.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })}
                    </div>
                  </div>

                  {/* Near kickoff badge */}
                  {isNearKickoff && (
                    <Badge className="text-xs bg-orange-100 text-orange-700 shrink-0">Soon</Badge>
                  )}

                  {/* Start button */}
                  <Button
                    size="sm"
                    onClick={() => handleStartMatch(match)}
                    disabled={isPending}
                    className="bg-green-600 hover:bg-green-700 text-white h-8 px-3 shrink-0"
                  >
                    <Play className="h-3 w-3 mr-1" />
                    Start
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {isLoading && (
        <div className="flex justify-center py-4">
          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-400" />
        </div>
      )}

      {!isLoading && upcoming.length === 0 && (
        <p className="text-center text-sm text-gray-400">No scheduled matches found.</p>
      )}
    </div>
  );
}
